import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { sleepRecoveryAPI } from '../../services/api'
import { useChartTheme } from '../../hooks/useChartTheme'
import ConstancyNotice from './ConstancyNotice'
import ExperimentCard from './ExperimentCard'
import UnrecordedMetricNote from './UnrecordedMetricNote'

const RANGES = ['4w', '3m', '6m', 'all']
const CHARTED = ['sleep_minutes', 'deep_minutes', 'rem_minutes', 'resting_hr', 'body_battery_gain']
const fmt = (v) => (v === null || v === undefined ? '—' : Math.round(v * 10) / 10)

function MetricPanel({ metric, chart }) {
  const { t } = useTranslation()
  const [grain, setGrain] = useState('weekly')
  // Implausible values are excluded, and null stays null so the line breaks (connectNulls=false).
  const data = metric.daily.map((p) => ({ date: p.date, value: p.implausible ? null : p.value }))
  const means = metric[grain] || []
  const change = metric.change_first_to_last_month

  return (
    <article className="bg-surface-container border rounded-lg p-4" data-testid={`metric-${metric.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold">{t(`sleepRecovery.metrics.${metric.key}`)}</h3>
        <dl className="flex flex-wrap gap-3 text-xs text-gray-600">
          <div><dt className="inline">{t('sleepRecovery.stats.mean')}: </dt><dd className="inline font-medium">{fmt(metric.mean)} {metric.unit}</dd></div>
          <div><dt className="inline">{t('sleepRecovery.stats.range')}: </dt><dd className="inline font-medium">{fmt(metric.min)}–{fmt(metric.max)}</dd></div>
          <div>
            <dt className="inline">{t('sleepRecovery.stats.change', { from: metric.first_month ?? '—', to: metric.last_month ?? '—' })}: </dt>
            <dd className="inline font-medium">{change === null || change === undefined ? '—' : `${change > 0 ? '+' : ''}${fmt(change)}`}</dd>
          </div>
        </dl>
      </div>
      {metric.implausible_count > 0 && (
        <p className="text-xs text-amber-700 mb-2">{t('sleepRecovery.implausible', { count: metric.implausible_count })}</p>
      )}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: chart.axis }} minTickGap={24} />
            <YAxis tick={{ fontSize: 11, fill: chart.axis }} domain={['auto', 'auto']} width={40} />
            <Tooltip {...(chart.tooltipProps || {})} />
            <Line type="monotone" dataKey="value" stroke={chart.accent} dot={false} connectNulls={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2">
        <div className="flex gap-2 text-xs mb-1">
          {['weekly', 'monthly'].map((g) => (
            <button key={g} type="button" onClick={() => setGrain(g)}
              className={`px-2 py-0.5 rounded ${grain === g ? 'bg-primary text-white' : 'border'}`}>
              {t(`sleepRecovery.grain.${g}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
          {means.map((m) => (
            <span key={m.period}>{m.period}: <span className="font-medium">{fmt(m.mean)}</span></span>
          ))}
        </div>
      </div>
    </article>
  )
}

/** VH-224: sleep & recovery trends. Self-contained so it can become a Health Cycle tab. */
export default function SleepRecoveryTab({ defaultRange = '3m' }) {
  const { t } = useTranslation()
  const chart = useChartTheme()
  const queryClient = useQueryClient()
  const [range, setRange] = useState(defaultRange)

  const query = useQuery({
    queryKey: ['sleep-recovery', range],
    queryFn: () => sleepRecoveryAPI.getTrends(range).then((r) => r.data),
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sleep-recovery'] })
  const start = useMutation({
    mutationFn: (target) => sleepRecoveryAPI.startExperiment({ target_minutes: target }),
    onSuccess: invalidate,
  })
  const end = useMutation({ mutationFn: (id) => sleepRecoveryAPI.endExperiment(id), onSuccess: invalidate })

  const data = query.data
  const running = data?.experiment?.status === 'running'

  return (
    <section className="space-y-4" data-testid="sleep-recovery-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('sleepRecovery.title')}</h2>
        <div className="flex gap-1" role="group" aria-label={t('sleepRecovery.rangeLabel')}>
          {RANGES.map((r) => (
            <button key={r} type="button" onClick={() => setRange(r)} aria-pressed={range === r}
              className={`px-3 py-1 text-sm rounded ${range === r ? 'bg-primary text-white' : 'border'}`}>
              {t(`sleepRecovery.ranges.${r}`)}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && <div className="h-48 bg-surface-container border rounded-lg animate-pulse" />}

      {query.isError && (
        <div className="border border-red-300 rounded-lg p-4 text-sm" role="alert">
          <p>{t('sleepRecovery.error')}</p>
          <button type="button" onClick={() => query.refetch()} className="mt-2 underline">{t('sleepRecovery.retry')}</button>
        </div>
      )}

      {data && (
        <>
          {!data.sufficient_data && (
            <p className="text-sm text-gray-600" data-testid="insufficient-data">
              {t('sleepRecovery.insufficient', { days: data.days_with_data, min: data.min_days_required })}
            </p>
          )}
          {(start.isError || end.isError) && (
            <p className="text-sm text-red-600" role="alert">{t('sleepRecovery.experiment.actionError')}</p>
          )}
          <ConstancyNotice
            constancy={data.constancy}
            canStart={!running}
            starting={start.isPending}
            onStartExperiment={(target) => start.mutate(target)}
          />
          <ExperimentCard experiment={data.experiment} ending={end.isPending} onEnd={(id) => end.mutate(id)} />
          {data.sufficient_data && (
            <div className="grid gap-4 md:grid-cols-2">
              {data.metrics.filter((m) => CHARTED.includes(m.key) && m.recorded).map((m) => (
                <MetricPanel key={m.key} metric={m} chart={chart} />
              ))}
            </div>
          )}
          {data.poor_recovery_nights.length > 0 && (
            <section className="bg-surface-container border rounded-lg p-4" data-testid="poor-recovery">
              <h3 className="text-sm font-semibold">{t('sleepRecovery.poorRecovery.title')}</h3>
              <p className="text-xs text-gray-600 mb-2">{t('sleepRecovery.poorRecovery.hint')}</p>
              <ul className="text-sm space-y-1">
                {data.poor_recovery_nights.map((n) => (
                  <li key={n.date}>
                    {t('sleepRecovery.poorRecovery.item', { date: n.date, hours: (n.sleep_minutes / 60).toFixed(1), gain: n.body_battery_gain })}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {data.unrecorded_metrics.map((key) => <UnrecordedMetricNote key={key} metricKey={key} />)}
        </>
      )}
    </section>
  )
}
